import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
} from '@monksflow/monks-ui';
import type { PersonaDto } from '@/lib/api/personas-client';
import { PersonaAvatar } from '@/shared/ui/PersonaAvatar/PersonaAvatar';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import { isDistinctTitle } from '@/shared/lib/persona-label';

const AVATAR_SIZE = 24;

interface LazyPersonaAvatarProps {
  name: string;
  photoAssetId?: string;
  scrollRootRef: RefObject<HTMLElement | null>;
}

// Defers PersonaAvatar (and its signed-URL fetch) until the row scrolls into
// the dropdown viewport — same optimization as the legacy implementation.
const LazyPersonaAvatar = ({ name, photoAssetId, scrollRootRef }: LazyPersonaAvatarProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: scrollRootRef.current, rootMargin: '50px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollRootRef, visible]);

  return (
    <div ref={ref} className="inline-flex shrink-0">
      {visible ? (
        <PersonaAvatar name={name} photoAssetId={photoAssetId} size={AVATAR_SIZE} />
      ) : (
        <div
          className="rounded-full bg-muted"
          style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
        />
      )}
    </div>
  );
};

const getPersonaTitle = (persona: PersonaDto): string =>
  persona.personaTitle ??
  persona.segmentName ??
  i18n.t('common:components.personaSelector.unknownSegment');

const getPersonaSubtitle = (persona: PersonaDto): string => {
  const age = persona.demographicsData?.data.age;
  const parts = isDistinctTitle(persona) ? [persona.segmentName, age] : [age];
  return parts.filter(Boolean).join(', ');
};

export interface PersonaSelectorProps {
  value: string[];
  onChange: (ids: string[]) => void;
  personas: PersonaDto[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

export const PersonaSelector = ({
  value,
  onChange,
  personas,
  placeholder,
  error,
  disabled = false,
}: PersonaSelectorProps) => {
  const { t } = useTranslation('common');
  const placeholderText = placeholder ?? t('components.personaSelector.searchPersonas');

  // Anchor the popup on the full-width chips row instead of the inner input
  // (which shrinks as chips fill in). Not strictly required at our current
  // form width, but kept as a guard against future regressions where many
  // chips squeeze the input down to its min size.
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const personaById = useMemo(
    () => new Map(personas.map((persona) => [persona.id, persona])),
    [personas],
  );

  // Combobox is controlled with the full PersonaDto array; we hydrate from
  // ids on the way in and project back to ids on the way out so the public
  // API (string[]) stays unchanged.
  const selectedPersonas = useMemo(
    () =>
      value
        .map((id) => personaById.get(id))
        .filter((persona): persona is PersonaDto => persona != null),
    [value, personaById],
  );

  const handleValueChange = useCallback(
    (next: PersonaDto[]) => {
      onChange(next.map((persona) => persona.id));
    },
    [onChange],
  );

  const itemToStringLabel = useCallback(
    (persona: PersonaDto) => `${getPersonaTitle(persona)} ${getPersonaSubtitle(persona)}`,
    [],
  );

  const itemToStringValue = useCallback((persona: PersonaDto) => persona.id, []);

  const isItemEqualToValue = useCallback((a: PersonaDto, b: PersonaDto) => a.id === b.id, []);

  return (
    <div className="flex flex-col gap-1">
      <Combobox<PersonaDto, true>
        multiple
        items={personas}
        value={selectedPersonas}
        onValueChange={handleValueChange}
        disabled={disabled}
        itemToStringLabel={itemToStringLabel}
        itemToStringValue={itemToStringValue}
        isItemEqualToValue={isItemEqualToValue}
      >
        <ComboboxChips ref={anchorRef} aria-invalid={error ? true : undefined}>
          {selectedPersonas.map((persona) => (
            <ComboboxChip
              key={persona.id}
              className="rounded-full bg-accent px-2.5 text-accent-foreground"
            >
              {getPersonaTitle(persona)}
            </ComboboxChip>
          ))}
          <ComboboxChipsInput
            placeholder={placeholderText}
            aria-label={placeholderText}
            className="pl-0"
          />
        </ComboboxChips>

        <ComboboxContent anchor={anchorRef} className="pointer-events-auto">
          <ComboboxList ref={listRef}>
            <ComboboxCollection>
              {(persona: PersonaDto) => (
                <ComboboxItem
                  key={persona.id}
                  value={persona}
                  className="data-[selected=true]:bg-accent/40"
                >
                  <LazyPersonaAvatar
                    name={getPersonaTitle(persona)}
                    photoAssetId={persona.photoAssetId}
                    scrollRootRef={listRef}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm text-foreground">{getPersonaTitle(persona)}</span>
                    <span className="text-xs text-muted-foreground">
                      {getPersonaSubtitle(persona)}
                    </span>
                  </div>
                </ComboboxItem>
              )}
            </ComboboxCollection>
            <ComboboxEmpty>{t('components.personaSelector.noPersonasFound')}</ComboboxEmpty>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

PersonaSelector.displayName = 'PersonaSelector';
